import type { Surface, SurfaceRegion } from './types';
import { isPaintableSurface } from './surfaceRoles';
import {surfaceRegions} from './surfaceRectangles';
import {compileSurfaceMask,surfaceMaskContains} from './surfaceGeometry';
export {surfaceRegions} from './surfaceRectangles';

/** Bounded, disjoint local rectangles. Absent regions mean the legacy full face. */
/** Authoring exclusions retain inspectable geometry but have no eligible paint. */
export function surfacePaintRegions(surface: Pick<Surface,'width'|'height'|'regions'|'contours'|'role'|'reservePanels'|'panelReservations'|'exteriorOcclusions'|'exteriorClip'>): readonly SurfaceRegion[] {
  const regions=surfaceRegions(surface);
  return isPaintableSurface(surface)?regions:[];
}
export function surfaceContains(surface: Pick<Surface,'width'|'height'|'regions'|'contours'|'role'|'reservePanels'|'panelReservations'|'exteriorOcclusions'|'exteriorClip'>, x:number,y:number): boolean {
  if(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)return surfaceMaskContains(surface,x,y);
  return surfacePaintRegions(surface).some(r=>x>=r.x && y>=r.y && x<r.x+r.width && y<r.y+r.height);
}

/** Exact rectangular subtraction. Horizontal bands are unioned, then identical
 * neighboring bands merge vertically before the supported complexity check. */
export function subtractSurfaceAreas(surface: Pick<Surface,'width'|'height'|'regions'>,cuts: readonly SurfaceRegion[]): SurfaceRegion[] {
  const source=surfaceRegions(surface);
  for(const cut of cuts)if(![cut.x,cut.y,cut.width,cut.height].every(Number.isFinite)||cut.x<0||cut.y<0||cut.width<=0||cut.height<=0||cut.x+cut.width>surface.width||cut.y+cut.height>surface.height)throw new Error('Protected area is outside the face.');
  type Interval={left:number;right:number};
  const merge=(intervals:Interval[]):Interval[]=>{
    const result:Interval[]=[];
    for(const interval of intervals.sort((a,b)=>a.left-b.left||a.right-b.right)){
      const previous=result.at(-1);
      if(previous&&interval.left<=previous.right)previous.right=Math.max(previous.right,interval.right);else result.push({...interval});
    }
    return result;
  };
  const levels=[...new Set([...source,...cuts].flatMap(rect=>[rect.y,rect.y+rect.height]))].sort((a,b)=>a-b),regions:SurfaceRegion[]=[];
  let previous=new Map<string,SurfaceRegion>();
  for(let index=0;index<levels.length-1;index++){
    const top=levels[index],bottom=levels[index+1];
    let intervals=merge(source.filter(rect=>rect.y<=top&&rect.y+rect.height>=bottom).map(rect=>({left:rect.x,right:rect.x+rect.width})));
    for(const cut of merge(cuts.filter(rect=>rect.y<=top&&rect.y+rect.height>=bottom).map(rect=>({left:rect.x,right:rect.x+rect.width}))))intervals=intervals.flatMap(interval=>cut.right<=interval.left||cut.left>=interval.right?[interval]:[{left:interval.left,right:Math.min(interval.right,cut.left)},{left:Math.max(interval.left,cut.right),right:interval.right}].filter(part=>part.right>part.left));
    const active=new Map<string,SurfaceRegion>();
    for(const interval of intervals){
      const key=`${interval.left}:${interval.right}`,above=previous.get(key);
      if(above&&above.y+above.height===top){above.height=bottom-above.y;active.set(key,above);}else{const region={x:interval.left,y:top,width:interval.right-interval.left,height:bottom-top};regions.push(region);active.set(key,region);}
    }
    previous=active;
  }
  surfaceRegions({...surface,regions});
  return regions;
}

/** Exterior directed boundary, with adjoining rectangle seams cancelled. Hole
 * loops run backwards so SVG nonzero filling and hit testing exclude openings. */
export function surfaceOutline(surface: Pick<Surface,'width'|'height'|'regions'|'contours'|'role'|'reservePanels'|'panelReservations'|'exteriorOcclusions'|'exteriorClip'>): string {
  if(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)return compileSurfaceMask(surface).outline;
  type Edge={start:number;end:number;direction:number};
  const horizontal=new Map<number,Edge[]>(),vertical=new Map<number,Edge[]>();
  const add=(map:Map<number,Edge[]>,at:number,start:number,end:number,direction:number)=>{const list=map.get(at)??[];list.push({start,end,direction});map.set(at,list);};
  for(const r of surfaceRegions(surface)){
    add(horizontal,r.y,r.x,r.x+r.width,1);add(horizontal,r.y+r.height,r.x,r.x+r.width,-1);
    add(vertical,r.x,r.y,r.y+r.height,-1);add(vertical,r.x+r.width,r.y,r.y+r.height,1);
  }
  type Vertex={x:number;y:number};const segments:{a:Vertex;b:Vertex}[]=[];
  for(const [isHorizontal,map] of [[true,horizontal],[false,vertical]] as const)for(const [at,edges] of map){
    const points=[...new Set(edges.flatMap(e=>[e.start,e.end]))].sort((a,b)=>a-b);
    for(let i=0;i<points.length-1;i++){const start=points[i],end=points[i+1],mid=(start+end)/2,direction=edges.reduce((sum,e)=>sum+(mid>e.start&&mid<e.end?e.direction:0),0);if(!direction)continue;
      const from=direction>0?start:end,to=direction>0?end:start;
      segments.push(isHorizontal?{a:{x:from,y:at},b:{x:to,y:at}}:{a:{x:at,y:from},b:{x:at,y:to}});
    }
  }
  const key=(p:Vertex)=>`${p.x},${p.y}`,remaining=new Set(segments),byStart=new Map<string,typeof segments>();
  for(const edge of segments){const k=key(edge.a),list=byStart.get(k)??[];list.push(edge);byStart.set(k,list);}
  let path='';
  while(remaining.size){let edge=remaining.values().next().value!,first=key(edge.a);path+=`M${edge.a.x} ${edge.a.y}`;
    while(remaining.has(edge)){remaining.delete(edge);path+=`L${edge.b.x} ${edge.b.y}`;if(key(edge.b)===first){path+='Z';break;}const next=byStart.get(key(edge.b))?.find(candidate=>remaining.has(candidate));if(!next)throw new Error('Surface boundary is not closed.');edge=next;}
  }
  return path;
}
/** Raster masks are for fill barriers, never authoritative ink. Pixel centres
 * match the drawn union; excluded space cannot connect two fill regions. */
export function surfaceRasterMask(surface: Surface,width:number,height:number): Uint8Array {
  if (!Number.isInteger(width)||!Number.isInteger(height)||width<1||height<1||width*height>4_194_304) throw new Error('Invalid surface raster.');
  if(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined){
    const compiled=compileSurfaceMask(surface),mask=new Uint8Array(width*height);if(!isPaintableSurface(surface))return mask;
    for(let y=0;y<height;y++){
      const worldY=(y+.5)*surface.height/height;
      for(const polygon of compiled.polygons){
        const intersections:number[]=[];
        for(const ring of polygon)for(let i=0;i<ring.length-1;i++){const a=ring[i],b=ring[i+1];if((a[1]>worldY)!==(b[1]>worldY))intersections.push(a[0]+(b[0]-a[0])*(worldY-a[1])/(b[1]-a[1]));}
        intersections.sort((a,b)=>a-b);
        for(let i=0;i<intersections.length;i+=2){const left=Math.max(0,Math.ceil(intersections[i]*width/surface.width-.5)),right=Math.min(width,Math.ceil(intersections[i+1]*width/surface.width-.5));mask.fill(1,y*width+left,y*width+right);}
      }
    }
    return mask;
  }
  const regions=surfacePaintRegions(surface),mask=new Uint8Array(width*height);
  for(const r of regions) for(let y=Math.max(0,Math.ceil(r.y*height/surface.height-.5));y<Math.min(height,Math.ceil((r.y+r.height)*height/surface.height-.5));y++) for(let x=Math.max(0,Math.ceil(r.x*width/surface.width-.5));x<Math.min(width,Math.ceil((r.x+r.width)*width/surface.width-.5));x++) mask[y*width+x]=1;
  return mask;
}
